import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { environment } from '../../../environments/environment';

interface KeywordMetrics {
  searchVolume: number;
  cpc: number;
  competition: number;
  competitionLevel: string | null;
}

interface CoreKeywordResult {
  topic: string;
  coreKeyword: string;
  topicMetrics?: KeywordMetrics | null;
  coreKeywordMetrics?: KeywordMetrics | null;
}

interface CoreKeywordMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  locationCode: number;
  languageName: string;
}

interface SerpResult {
  position: number;
  domain: string;
  url: string;
  title: string;
}

interface SerpResponse {
  keyword: string;
  results: SerpResult[];
}

interface SerpMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  keyword: string;
  locationCode: number;
  languageName: string;
}

interface Entity {
  keyword: string;
  normalizedKeyword: string;
  searchVolume: number;
  keywordDifficulty: number;
  cpc?: number;
  coreKeyword: string | null;
  sources: ('related' | 'suggestions' | 'ranked_keywords' | 'ranked_keywords_filtered')[];
  rankedFromPages: string[] | Array<{
    url: string;
    title: string;
    domain: string;
    position: number;
  }>;
  similarityScore: number;
}

interface SharedEntity extends Entity {
  domainCount: number;
  domains: string[];
}

interface EntityCostBreakdown {
  relatedKeywords?: number;
  suggestions?: number;
  rankedKeywords?: number;
  rankedKeywordsFiltered?: number;
  total: number;
}

interface EntityStats {
  totalEntities: number;
  fromRelated?: number;
  fromSuggestions?: number;
  fromRankedKeywords?: number;
  fromRankedKeywordsFiltered?: number;
  uniqueEntities: number;
  domainsAnalyzed?: number;
}

interface FilterDetails {
  inputTopic: string;
  discoveredCoreKeyword: string;
  additionalCoreKeywords: Array<{
    coreKeyword: string;
    bestEntity: string;
    similarityScore: number;
    entityCount: number;
  }>;
}

interface SharedEntityStats {
  totalDiscovered: number;
  afterDeduplication: number;
  afterSharedFilter: number;
  fromInitialDiscovery: number;
  fromFilteredDiscovery: number;
}

interface SharedEntityCostBreakdown {
  initialDiscovery: number;
  filteredDiscovery: number;
  total: number;
}

interface SharedEntityMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  domains: string[];
  locationCode: number;
  languageName: string;
}

interface DomainCoverage {
  domain: string;
  entitiesCovered: number;
  totalKeywordsRanked: number;
  coveragePercentage: number;
  urls: Array<{
    url: string;
    title: string;
    position: number;
    entitiesCount: number;
  }>;
  missingEntities: string[];
}

interface DomainCoverageMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  topic: string;
  domains: string[];
  locationCode: number;
  languageName: string;
}

interface DomainCoverageStats {
  totalEntities: number;
  totalDomains: number;
  totalApiCalls: number;
}

interface DomainCoverageCostBreakdown {
  rankedKeywords: number;
  total: number;
}

interface FilteredEntityMetadata {
  dataSource: 'database' | 'api' | 'unknown';
  lastUpdated: string | null;
  domainsAnalyzed: number;
  coreKeywordsUsedAsFilters: string[];
  locationCode: number;
  languageName: string;
}

@Component({
  selector: 'app-topic-share-3',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './topic-share-3.html',
  styleUrl: './topic-share-3.css'
})
export class TopicShareComponent3 implements OnInit {
  topic: string = '';
  searchTopic: string = '';
  loading = false;
  loadingSerp = false;
  error: string | null = null;
  result: CoreKeywordResult | null = null;
  metadata: CoreKeywordMetadata | null = null;
  
  serpResults: {
    topic: SerpResponse | null;
    coreKeyword: SerpResponse | null;
  } = { topic: null, coreKeyword: null };

  serpMetadata: {
    topic: SerpMetadata | null;
    coreKeyword: SerpMetadata | null;
  } = { topic: null, coreKeyword: null };

  // Cost tracking
  totalCost: number = 0;
  historicalTotalCost: number = 0;
  coreKeywordCost: number = 0;
  serpCost: number = 0;

  // Entity discovery
  loadingEntities: boolean = false;
  entities: Entity[] = [];
  entityCostBreakdown: EntityCostBreakdown | null = null;
  entityStats: EntityStats | null = null;
  entityMetadata: {
    dataSource: 'database' | 'api' | 'unknown';
    lastUpdated: string | null;
    topic: string;
    coreKeyword: string;
    locationCode: number;
    languageName: string;
    pagesAnalyzed?: number;
  } | null = null;
  urlsLimited: boolean = false;
  totalSerpUrls: number = 0;
  excludedUrls: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
  entitiesDisplayLimit: number = 5;
  showAllEntities: boolean = false;

  // SERP results pagination
  serpDisplayLimit: number = 5;
  showAllSerpResults: { topic: boolean; coreKeyword: boolean } = { topic: false, coreKeyword: false };

  // Entity filtering
  filteredCoreKeywords: string[] = [];
  filterDetails: FilterDetails | null = null;
  loadingFilter: boolean = false;

  // Filtered entity discovery
  filteredEntities: Entity[] = [];
  loadingFilteredEntities: boolean = false;
  filteredEntityCostBreakdown: EntityCostBreakdown | null = null;
  filteredEntityStats: EntityStats | null = null;
  filteredEntityMetadata: FilteredEntityMetadata | null = null;
  showAllFilteredEntities: boolean = false;
  domainTotalCounts: Record<string, number> = {};

  // Shared entity list
  sharedEntities: SharedEntity[] = [];
  loadingSharedEntities: boolean = false;
  sharedEntityStats: SharedEntityStats | null = null;
  sharedEntityCostBreakdown: SharedEntityCostBreakdown | null = null;
  sharedEntityMetadata: SharedEntityMetadata | null = null;
  showAllSharedEntities: boolean = false;

  // Domain coverage
  domainCoverageResults: DomainCoverage[] = [];
  loadingDomainCoverage: boolean = false;
  domainCoverageMetadata: DomainCoverageMetadata | null = null;
  domainCoverageCostBreakdown: DomainCoverageCostBreakdown | null = null;
  domainCoverageStats: DomainCoverageStats | null = null;

  // Custom domains to include in analysis
  // These are domains the user wants to analyze even if they don't appear in SERP results.
  // Common use case: comparing two domains that don't rank in top 10 for the topic.
  customDomains: string[] = [];
  newCustomDomain: string = '';

  // Helper to check if topic and core keyword are the same
  get isTopicSameAsCoreKeyword(): boolean {
    if (!this.searchTopic || !this.result?.coreKeyword) return false;
    return this.searchTopic.toLowerCase().trim() === this.result.coreKeyword.toLowerCase().trim();
  }
  
  // Defaults
  readonly defaultLocationCode: number = 2840;
  readonly defaultLanguageName: string = 'English';

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute
  ) {}

  /**
   * On init, check for query params to pre-fill topic and custom domains.
   * This enables deep linking from Page Comparison when Topic Share data isn't available.
   * 
   * Supported query params:
   * - topic: The topic to analyze (e.g., "website feedback")
   * - domains: Comma-separated list of domains to include (e.g., "site1.com,site2.com")
   * 
   * Example URL: /topic-share-3?topic=website%20feedback&domains=yoursite.com,competitor.com
   */
  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      // Pre-fill topic if provided
      if (params['topic']) {
        this.topic = params['topic'];
      }
      
      // Pre-fill custom domains if provided
      if (params['domains']) {
        const domainList = params['domains'].split(',').map((d: string) => d.trim().toLowerCase());
        domainList.forEach((domain: string) => {
          if (domain && !this.customDomains.includes(domain)) {
            this.customDomains.push(domain);
          }
        });
      }
    });
  }

  // ============================================
  // Custom Domain Management
  // ============================================

  /**
   * Normalize a domain string by removing protocol, www prefix, and trailing paths.
   */
  private normalizeDomain(domain: string): string {
    return domain
      .toLowerCase()
      .trim()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0];
  }

  /**
   * Add a custom domain to the analysis list.
   * Custom domains are included in coverage analysis even if they don't appear in SERP results.
   */
  addCustomDomain(): void {
    if (!this.newCustomDomain.trim()) {
      return;
    }

    const normalized = this.normalizeDomain(this.newCustomDomain);
    
    if (!normalized) {
      return;
    }

    // Check if already in custom domains
    if (this.customDomains.includes(normalized)) {
      this.error = `Domain "${normalized}" is already in the custom domains list.`;
      return;
    }

    // Check if already in SERP domains
    const serpDomains = this.getSerpDomains();
    if (serpDomains.includes(normalized)) {
      this.error = `Domain "${normalized}" already appears in SERP results.`;
      return;
    }

    this.customDomains.push(normalized);
    this.newCustomDomain = '';
    this.error = null;

    console.log(`✅ Added custom domain: ${normalized}`);
  }

  /**
   * Remove a custom domain from the analysis list.
   */
  removeCustomDomain(domain: string): void {
    const index = this.customDomains.indexOf(domain);
    if (index !== -1) {
      this.customDomains.splice(index, 1);
      console.log(`🗑️ Removed custom domain: ${domain}`);
    }
  }

  /**
   * Get domains from SERP results only (without custom domains).
   */
  getSerpDomains(): string[] {
    const domainSet = new Set<string>();
    
    if (this.serpResults.topic?.results) {
      this.serpResults.topic.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    if (this.serpResults.coreKeyword?.results) {
      this.serpResults.coreKeyword.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    return Array.from(domainSet);
  }

  /**
   * Check if a domain is from SERP (not custom).
   */
  isDomainFromSerp(domain: string): boolean {
    return this.getSerpDomains().includes(domain.toLowerCase().trim().replace(/^www\./, ''));
  }

  async discoverCoreKeyword() {
    if (!this.topic.trim()) {
      this.error = 'Please enter a topic';
      return;
    }

    this.searchTopic = this.topic.trim();
    this.loading = true;
    this.error = null;
    this.result = null;
    this.metadata = null;
    this.serpResults = { topic: null, coreKeyword: null };
    this.serpMetadata = { topic: null, coreKeyword: null };
    this.loadingSerp = false;
    this.totalCost = 0;
    this.historicalTotalCost = 0;
    this.coreKeywordCost = 0;
    this.serpCost = 0;
    this.entities = [];
    this.entityCostBreakdown = null;
    this.entityStats = null;
    this.filteredCoreKeywords = [];
    this.filterDetails = null;
    this.entities = [];
    this.entityCostBreakdown = null;
    this.entityStats = null;
    this.entityMetadata = null;
    this.filteredCoreKeywords = [];
    this.filterDetails = null;
    this.filteredEntities = [];
    this.filteredEntityCostBreakdown = null;
    this.filteredEntityStats = null;
    this.sharedEntities = [];
    this.sharedEntityStats = null;
    this.sharedEntityCostBreakdown = null;
    this.sharedEntityMetadata = null;
    this.domainCoverageResults = [];
    this.domainCoverageMetadata = null;
    this.domainCoverageCostBreakdown = null;
    this.domainCoverageStats = null;

    try {
      const API_URL = environment.apiUrl + '/v1';
      
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/core-keyword`,
        {
          topic: this.searchTopic
        }
      ).toPromise();

      if (response.success) {
        this.result = response.data;
        this.metadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          topic: this.searchTopic.toLowerCase().trim(),
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };
        
        if (this.metadata?.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
          this.coreKeywordCost = response.data.tasks[0].cost;
          this.totalCost += this.coreKeywordCost;
        }
        
        await this.discoverSerpResults();
      } else {
        this.error = response.message || 'Failed to discover core keyword';
        this.metadata = null;
      }
    } catch (error: any) {
      console.error('Error:', error);
      this.error = error.error?.message || error.message || 'Failed to discover core keyword';
      this.metadata = null;
    } finally {
      this.loading = false;
    }
  }

  async discoverSerpResults() {
    if (!this.result || !this.result.coreKeyword) {
      return;
    }

    this.loadingSerp = true;
    const API_URL = environment.apiUrl + '/v1';

    const normalizedTopic = this.searchTopic.toLowerCase().trim();
    const normalizedCoreKeyword = this.result.coreKeyword.toLowerCase().trim();
    const isSame = normalizedTopic === normalizedCoreKeyword;

    try {
      if (isSame) {
        const response = await this.http.post<any>(
          `${API_URL}/dataforseo/topic-share/serp`,
          {
            keyword: this.searchTopic
          }
        ).toPromise();

        if (response.success) {
          const serpResponse: SerpResponse = {
            keyword: response.data.keyword,
            results: response.data.results
          };
          const serpMeta: SerpMetadata = response.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: response.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };

          if (serpMeta.dataSource === 'api' && response.data?.tasks?.[0]?.cost) {
            this.serpCost = response.data.tasks[0].cost;
            this.totalCost += this.serpCost;
          }

          this.serpResults.topic = serpResponse;
          this.serpResults.coreKeyword = serpResponse;
          this.serpMetadata.topic = serpMeta;
          this.serpMetadata.coreKeyword = serpMeta;
        }
      } else {
        const [topicResponse, coreKeywordResponse] = await Promise.allSettled([
          this.http.post<any>(
            `${API_URL}/dataforseo/topic-share/serp`,
            { keyword: this.searchTopic }
          ).toPromise(),
          this.http.post<any>(
            `${API_URL}/dataforseo/topic-share/serp`,
            { keyword: this.result.coreKeyword }
          ).toPromise()
        ]);

        if (topicResponse.status === 'fulfilled' && topicResponse.value.success) {
          this.serpResults.topic = {
            keyword: topicResponse.value.data.keyword,
            results: topicResponse.value.data.results
          };
          this.serpMetadata.topic = topicResponse.value.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: topicResponse.value.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };
          
          if (this.serpMetadata.topic?.dataSource === 'api' && topicResponse.value.data?.tasks?.[0]?.cost) {
            const topicCost = topicResponse.value.data.tasks[0].cost;
            this.serpCost += topicCost;
            this.totalCost += topicCost;
          }
        }

        if (coreKeywordResponse.status === 'fulfilled' && coreKeywordResponse.value.success) {
          this.serpResults.coreKeyword = {
            keyword: coreKeywordResponse.value.data.keyword,
            results: coreKeywordResponse.value.data.results
          };
          this.serpMetadata.coreKeyword = coreKeywordResponse.value.metadata || {
            dataSource: 'unknown',
            lastUpdated: null,
            keyword: coreKeywordResponse.value.data.keyword,
            locationCode: this.defaultLocationCode,
            languageName: this.defaultLanguageName
          };
          
          if (this.serpMetadata.coreKeyword?.dataSource === 'api' && coreKeywordResponse.value.data?.tasks?.[0]?.cost) {
            const coreKeywordSerpCost = coreKeywordResponse.value.data.tasks[0].cost;
            this.serpCost += coreKeywordSerpCost;
            this.totalCost += coreKeywordSerpCost;
          }
        }
      }
    } catch (error: any) {
      console.error('Error fetching SERP results:', error);
    } finally {
      this.loadingSerp = false;
    }
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  }

  getAllSerpUrls(): { 
    urls: string[], 
    limited: boolean, 
    total: number,
    excluded: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }>
  } {
    const urls = new Set<string>();
    const includedResults: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
    const excludedResults: Array<{ url: string; title: string; domain: string; source: 'topic' | 'coreKeyword' }> = [];
    
    if (this.serpResults.topic?.results) {
      for (const result of this.serpResults.topic.results) {
        if (urls.size >= 10) {
          excludedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'topic'
          });
        } else {
          urls.add(result.url);
          includedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'topic'
          });
        }
      }
    }
    
    if (this.serpResults.coreKeyword?.results && !this.isTopicSameAsCoreKeyword) {
      for (const result of this.serpResults.coreKeyword.results) {
        if (urls.has(result.url)) {
          continue;
        }
        if (urls.size >= 10) {
          excludedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'coreKeyword'
          });
        } else {
          urls.add(result.url);
          includedResults.push({
            url: result.url,
            title: result.title,
            domain: result.domain,
            source: 'coreKeyword'
          });
        }
      }
    }
    
    const collectedUrls = Array.from(urls);
    const totalAvailable = includedResults.length + excludedResults.length;
    const wasLimited = excludedResults.length > 0;
    
    return {
      urls: collectedUrls,
      limited: wasLimited,
      total: totalAvailable,
      excluded: excludedResults
    };
  }

  get hasSerpResults(): boolean {
    return !!(this.serpResults.topic?.results?.length || this.serpResults.coreKeyword?.results?.length);
  }

  async discoverEntities() {
    if (!this.hasSerpResults || !this.searchTopic || !this.result?.coreKeyword) {
      this.error = 'Please discover SERP results and core keyword first';
      return;
    }

    this.loadingEntities = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const urlData = this.getAllSerpUrls();
      const serpPageUrls = urlData.urls;
      this.urlsLimited = urlData.limited;
      this.totalSerpUrls = urlData.total;
      this.excludedUrls = urlData.excluded;
      
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities`,
        {
          topic: this.searchTopic,
          coreKeyword: this.result.coreKeyword,
          serpPageUrls: serpPageUrls
        }
      ).toPromise();

      if (response.success) {
        this.entities = response.data.entities || [];
        this.entityCostBreakdown = response.data.costBreakdown || null;
        this.entityStats = response.data.stats || null;
        this.entityMetadata = response.metadata || {
          dataSource: 'unknown',
          lastUpdated: null,
          topic: this.searchTopic.toLowerCase().trim(),
          coreKeyword: this.result.coreKeyword.toLowerCase().trim(),
          locationCode: this.defaultLocationCode,
          languageName: this.defaultLanguageName
        };

        if (this.entityCostBreakdown?.total) {
          if (this.entityMetadata?.dataSource === 'api') {
            this.totalCost += this.entityCostBreakdown.total;
          } else if (this.entityMetadata?.dataSource === 'database') {
            this.historicalTotalCost += this.entityCostBreakdown.total;
          }
        }

        console.log(`✅ Discovered ${this.entities.length} entities (cost: $${this.entityCostBreakdown?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to discover entities';
      }
    } catch (error: any) {
      console.error('Error discovering entities:', error);
      this.error = error.error?.message || error.message || 'Failed to discover entities';
    } finally {
      this.loadingEntities = false;
    }
  }

  async filterEntities() {
    if (!this.entities.length || !this.searchTopic || !this.result?.coreKeyword) {
      this.error = 'Please discover entities first';
      return;
    }

    this.loadingFilter = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/filter-entities`,
        {
          topic: this.searchTopic,
          coreKeyword: this.result.coreKeyword,
          entities: this.entities
        }
      ).toPromise();

      if (response.success) {
        this.filteredCoreKeywords = response.data.filteredCoreKeywords || [];
        this.filterDetails = response.data.selectionDetails || null;
        console.log(`✅ Filtered to ${this.filteredCoreKeywords.length} core keywords`);
      } else {
        this.error = response.message || 'Failed to filter entities';
      }
    } catch (error: any) {
      console.error('Error filtering entities:', error);
      this.error = error.error?.message || error.message || 'Failed to filter entities';
    } finally {
      this.loadingFilter = false;
    }
  }

  /**
   * Get all unique domains for analysis: SERP domains + custom domains.
   * This merges domains from SERP results with any custom domains the user has added.
   * Custom domains enable analysis of sites that don't rank in the top 10.
   */
  getUniqueDomains(): string[] {
    const domainSet = new Set<string>();
    
    // Add SERP domains
    if (this.serpResults.topic?.results) {
      this.serpResults.topic.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    if (this.serpResults.coreKeyword?.results) {
      this.serpResults.coreKeyword.results.forEach(result => {
        const domain = result.domain.toLowerCase().trim().replace(/^www\./, '');
        domainSet.add(domain);
      });
    }
    
    // Add custom domains
    this.customDomains.forEach(domain => {
      domainSet.add(domain.toLowerCase().trim().replace(/^www\./, ''));
    });
    
    return Array.from(domainSet);
  }

  async discoverFilteredEntities() {
    if (!this.filteredCoreKeywords.length) {
      this.error = 'Please filter entities first';
      return;
    }

    const domains = this.getUniqueDomains();
    
    if (domains.length === 0) {
      this.error = 'No domains available from SERP results';
      return;
    }

    this.loadingFilteredEntities = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share/discover-entities-filtered`,
        {
          domains: domains,
          coreKeywords: this.filteredCoreKeywords
        }
      ).toPromise();

      if (response.success) {
        this.filteredEntities = response.data.entities || [];
        this.filteredEntityCostBreakdown = response.data.costBreakdown || null;
        this.filteredEntityStats = response.data.stats || null;
        this.filteredEntityMetadata = response.metadata || null;
        this.domainTotalCounts = response.metadata?.domainTotalCounts || {};

        if (this.filteredEntityCostBreakdown?.total) {
          const filteredDataSource = response.metadata?.dataSource;
          if (filteredDataSource === 'api') {
            this.totalCost += this.filteredEntityCostBreakdown.total;
          } else if (filteredDataSource === 'database') {
            this.historicalTotalCost += this.filteredEntityCostBreakdown.total;
          }
        }

        console.log(`✅ Discovered ${this.filteredEntities.length} filtered entities (cost: $${this.filteredEntityCostBreakdown?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to discover filtered entities';
      }
    } catch (error: any) {
      console.error('Error discovering filtered entities:', error);
      this.error = error.error?.message || error.message || 'Failed to discover filtered entities';
    } finally {
      this.loadingFilteredEntities = false;
    }
  }

  async buildSharedEntityList() {
    if (!this.entities.length || !this.filteredEntities.length) {
      this.error = 'Please complete initial entity discovery and filtered entity discovery first';
      return;
    }

    if (!this.result?.coreKeyword) {
      this.error = 'Core keyword is required';
      return;
    }

    const urlData = this.getAllSerpUrls();
    const domains = this.getUniqueDomains();
    
    if (domains.length === 0) {
      this.error = 'No domains available from SERP results';
      return;
    }

    this.loadingSharedEntities = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share-3/build-shared-entities`,
        {
          topic: this.searchTopic,
          coreKeyword: this.result.coreKeyword,
          serpPageUrls: urlData.urls,
          domains: domains,
          coreKeywords: this.filteredCoreKeywords
        }
      ).toPromise();

      if (response.success) {
        this.sharedEntities = response.data.sharedEntities || [];
        this.sharedEntityStats = response.data.stats || null;
        this.sharedEntityCostBreakdown = response.data.costBreakdown || null;
        this.sharedEntityMetadata = response.metadata || null;

        if (this.sharedEntityCostBreakdown?.total) {
          // Shared entity build merges prior discoveries and reuses stored costs.
          // Treat as historical/reference cost, not new spend for this run.
          this.historicalTotalCost += this.sharedEntityCostBreakdown.total;
        }

        console.log(`✅ Built shared entity list: ${this.sharedEntities.length} entities (cost: $${this.sharedEntityCostBreakdown?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to build shared entity list';
      }
    } catch (error: any) {
      console.error('Error building shared entity list:', error);
      this.error = error.error?.message || error.message || 'Failed to build shared entity list';
    } finally {
      this.loadingSharedEntities = false;
    }
  }

  async checkDomainCoverage() {
    if (!this.sharedEntities.length) {
      this.error = 'Please build shared entity list first';
      return;
    }

    const domains = this.getUniqueDomains();
    if (domains.length === 0) {
      this.error = 'No domains found in SERP results';
      return;
    }

    const sharedEntityList = this.sharedEntities.map(e => e.normalizedKeyword);

    this.loadingDomainCoverage = true;
    this.error = null;
    const API_URL = environment.apiUrl + '/v1';

    try {
      const response = await this.http.post<any>(
        `${API_URL}/dataforseo/topic-share-3/check-domain-coverage`,
        {
          topic: this.searchTopic,
          domains: domains,
          sharedEntityList: sharedEntityList
        }
      ).toPromise();

      if (response.success) {
        this.domainCoverageResults = response.data.domains || [];
        this.domainCoverageStats = response.data.stats || null;
        this.domainCoverageCostBreakdown = response.data.costBreakdown || null;
        this.domainCoverageMetadata = response.metadata || null;

        // Check for failed domains
        if (response.data.failedDomains && response.data.failedDomains.length > 0) {
          const failedDomainsList = response.data.failedDomains.map((fd: any) => `${fd.domain}: ${fd.error}`).join(', ');
          this.error = `Some domains failed to process: ${failedDomainsList}`;
          console.warn(`⚠️  Failed domains:`, response.data.failedDomains);
        }

        if (this.domainCoverageCostBreakdown?.total) {
          if (this.domainCoverageMetadata?.dataSource === 'api') {
            this.totalCost += this.domainCoverageCostBreakdown.total;
          } else if (this.domainCoverageMetadata?.dataSource === 'database') {
            this.historicalTotalCost += this.domainCoverageCostBreakdown.total;
          }
        }

        console.log(`✅ Checked domain coverage for ${this.domainCoverageResults.length} domains (cost: $${this.domainCoverageCostBreakdown?.total.toFixed(4) || '0.0000'})`);
      } else {
        this.error = response.message || 'Failed to check domain coverage';
      }
    } catch (error: any) {
      console.error('Error checking domain coverage:', error);
      this.error = error.error?.message || error.message || 'Failed to check domain coverage';
    } finally {
      this.loadingDomainCoverage = false;
    }
  }

  getSimilarityColorClass(score: number): string {
    if (score >= 0.8) return 'similarity-high';
    if (score >= 0.6) return 'similarity-medium';
    if (score >= 0.4) return 'similarity-low';
    return 'similarity-very-low';
  }

  formatSimilarity(score: number): string {
    return (score * 100).toFixed(1) + '%';
  }

  // Get displayed entities (limited)
  getDisplayedEntities(): Entity[] {
    return this.showAllEntities 
      ? this.entities 
      : this.entities.slice(0, this.entitiesDisplayLimit);
  }

  // Toggle show all entities
  toggleShowAllEntities() {
    this.showAllEntities = !this.showAllEntities;
  }

  // Check if there are more entities to show
  hasMoreEntities(): boolean {
    return this.entities.length > this.entitiesDisplayLimit;
  }

  isRankedFromPagesArray(pages: any): boolean {
    return Array.isArray(pages) && pages.length > 0 && typeof pages[0] === 'object' && 'url' in pages[0];
  }

  getRankedFromPagesAsObjects(pages: any): Array<{ url: string; title: string; domain: string; position: number }> {
    if (this.isRankedFromPagesArray(pages)) {
      return pages as Array<{ url: string; title: string; domain: string; position: number }>;
    }
    return [];
  }

  getRankedFromPagesAsStrings(pages: any): string[] {
    if (Array.isArray(pages)) {
      if (pages.length > 0 && typeof pages[0] === 'string') {
        return pages as string[];
      }
    }
    return [];
  }

  isRankedFromPagesEmpty(pages: any): boolean {
    if (!pages) return true;
    if (Array.isArray(pages)) {
      return pages.length === 0;
    }
    return true;
  }

  // Show only run spend for the current step.
  getRunCostByDataSource(total: number | null | undefined, dataSource: string | null | undefined): number {
    return dataSource === 'api' ? (total || 0) : 0;
  }

  // Show cached reference spend separately from run spend.
  getHistoricalCostByDataSource(total: number | null | undefined, dataSource: string | null | undefined): number {
    return dataSource === 'database' ? (total || 0) : 0;
  }
}

